import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Proposal, ProposalDocument, InsertProposal, InsertProposalDocument } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";

// Hook for proposals operations
export function useProposals(tripId: number) {
  const queryClient = useQueryClient();
  
  // Get all proposals for a trip
  const proposalsQuery = useQuery({
    queryKey: ['/api/trips', tripId, 'proposals'],
    queryFn: async () => {
      const response = await fetch(`/api/trips/${tripId}/proposals`);
      if (!response.ok) {
        throw new Error('Failed to fetch proposals');
      }
      return response.json() as Promise<Proposal[]>;
    },
    enabled: !!tripId,
  });

  // Create a new proposal
  const createProposalMutation = useMutation({
    mutationFn: async (proposal: InsertProposal) => {
      const response = await apiRequest(
        'POST',
        '/api/proposals',
        proposal
      );
      return response as Proposal;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/trips', tripId, 'proposals'] });
    },
  });

  // Update a proposal
  const updateProposalMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<InsertProposal> }) => {
      const response = await apiRequest(
        'PATCH',
        `/api/proposals/${id}`,
        data
      );
      return response as Proposal;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/trips', tripId, 'proposals'] });
    },
  });

  // Delete a proposal
  const deleteProposalMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest(
        'DELETE',
        `/api/proposals/${id}`
      );
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/trips', tripId, 'proposals'] });
    },
  });

  // Generate a proposal using AI
  const generateProposalMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest(
        'POST', // First parameter is the method
        `/api/trips/${tripId}/proposals/generate` // Second parameter is the URL
      );
      return response as Proposal;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/trips', tripId, 'proposals'] });
    },
  });

  return {
    proposals: proposalsQuery.data || [],
    isLoading: proposalsQuery.isLoading,
    error: proposalsQuery.error,
    createProposal: createProposalMutation.mutate,
    isCreating: createProposalMutation.isPending,
    updateProposal: updateProposalMutation.mutate,
    isUpdating: updateProposalMutation.isPending,
    deleteProposal: deleteProposalMutation.mutate,
    isDeleting: deleteProposalMutation.isPending,
    generateProposal: generateProposalMutation.mutate,
    isGenerating: generateProposalMutation.isPending,
  };
}

// Hook for getting a single proposal
export function useProposal(id: number | null) {
  return useQuery({
    queryKey: ['/api/proposals', id],
    queryFn: async () => {
      if (!id) return null;
      const response = await fetch(`/api/proposals/${id}`);
      if (!response.ok) {
        throw new Error('Failed to fetch proposal');
      }
      return response.json() as Promise<Proposal>;
    },
    enabled: !!id,
  });
}

// Hook for proposal documents operations
export function useProposalDocuments(proposalId: number | null) {
  const queryClient = useQueryClient();
  
  // Get all documents for a proposal
  const documentsQuery = useQuery({
    queryKey: ['/api/proposals', proposalId, 'documents'],
    queryFn: async () => {
      if (!proposalId) return [];
      const response = await fetch(`/api/proposals/${proposalId}/documents`);
      if (!response.ok) {
        throw new Error('Failed to fetch proposal documents');
      }
      return response.json() as Promise<ProposalDocument[]>;
    },
    enabled: !!proposalId,
  });

  // Create a new document
  const createDocumentMutation = useMutation({
    mutationFn: async (document: InsertProposalDocument) => {
      const response = await apiRequest(
        'POST',
        '/api/proposal-documents',
        document
      );
      return response as ProposalDocument;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/proposals', proposalId, 'documents'] });
    },
  });

  // Delete a document
  const deleteDocumentMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest(
        'DELETE',
        `/api/proposal-documents/${id}`
      );
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/proposals', proposalId, 'documents'] });
    },
  });

  return {
    documents: documentsQuery.data || [],
    isLoading: documentsQuery.isLoading,
    error: documentsQuery.error,
    createDocument: createDocumentMutation.mutate,
    isCreating: createDocumentMutation.isPending,
    deleteDocument: deleteDocumentMutation.mutate,
    isDeleting: deleteDocumentMutation.isPending,
  };
}